import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../components/common/HtmlContent';
import useHashScroll from '../../../hooks/useHashScroll';

/**
 * DataPage Component
 * 
 * Displays data tools content like the legacy HTML site
 */
const DataPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Data intro content from the legacy HTML
  const dataIntroHtml = `
    <p>
    While qualitative research methods like interviews provide rich insights, quantitative data offers a different perspective that can help validate findings, identify patterns, and measure the scale of user behaviors.
    </p>

    <p>
    Data-driven research approaches complement qualitative methods by providing objective measurements and helping identify trends across larger user populations.
    </p>

    <p>
    The following tools focus on collecting and analyzing quantitative data to inform product decisions.
    </p>
  `;

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>Templates</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // Templates HTML for each tool
  const analyticsTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/data/analytics.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_klB2_IE=/" className="link-table">Miro</a>
    </>
  );

  const abTestingTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/data/ab-testing.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_klCr2jE=/" className="link-table">Miro</a>
    </>
  );

  const questionnaireTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/research/empathise/data/questionnaire.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_klD5iR8=/" className="link-table">Miro</a>
    </>
  );

  // Creator HTML
  const naCreator = <a href="">N/A</a>;
  const kohavihCreator = <a href="https://www.exp-platform.com/Documents/GuideControlledExperiments.pdf">Ron Kohavi</a>;

  return (
    <>
      {/* Data Intro */}
      <div className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/research">Research Tools</Link>
              &nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>
              &nbsp;&nbsp;
              <Link to="/research#empathise">Empathise</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/research/empathise/data">Data</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={dataIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#analytics">Analytics</Link></div>
            <div><Link className="tool-section-cta" to="#ab-testing">A/B Testing</Link></div>
            <div><Link className="tool-section-cta" to="#questionnaire">Questionnaire</Link></div>
          </div>
        </div>
      </div>

      {/* Analytics Section */}
      <main className="case-study">
        <span className="case-tool-title" id="analytics">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="analytics">
            <span className="tool-title-number">
              <Link to="/empathise/data#analytics">Analytics</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/data">Data</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/data#analytics"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-analytics" 
            id="tab-analytics-overview" 
            defaultChecked
          />
          <label htmlFor="tab-analytics-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Data", "20/08/20", analyticsTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                Analytics involves collecting, measuring, and analyzing data about how users interact with your product. This provides objective insights into user behavior at scale.
                </p>
                
                <p>
                By tracking metrics like page views, conversion rates, user flows, and engagement times, you can identify patterns, pain points, and opportunities that might not be apparent through qualitative research alone.
                </p>
                
                <p>
                Analytics is particularly valuable for validating hypotheses, monitoring the impact of changes, and understanding how users actually use your product in the real world.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/analytics-user-experience/" className="link-table">Using Analytics to Improve UX and User Experience</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/article/google-analytics-the-big-picture" className="link-table">Google Analytics: The Big Picture</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-analytics" 
            id="tab-analytics-template"
          />
          <label htmlFor="tab-analytics-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_klB2_IE=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Analytics Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* A/B Testing Section */}
      <main className="case-study">
        <span className="case-tool-title" id="ab-testing">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="ab-testing">
            <span className="tool-title-number">
              <Link to="/empathise/data#ab-testing">A/B Testing</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/data">Data</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/data#ab-testing"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-ab-testing" 
            id="tab-ab-testing-overview" 
            defaultChecked
          />
          <label htmlFor="tab-ab-testing-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Data", "20/08/20", abTestingTemplates, kohavihCreator)}
              <div className="tool-opening">
                <p>
                A/B testing (or split testing) compares two versions of a design or feature to determine which one performs better according to defined metrics. It provides empirical evidence for design decisions.
                </p>
                
                <p>
                By randomly distributing users between variant A and variant B, then measuring their behavior, you can determine with statistical confidence which version better achieves your goals.
                </p>
                
                <p>
                This method is particularly valuable for optimizing conversion funnels, testing design changes, validating new features, and making data-informed improvements to your product.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.optimizely.com/optimization-glossary/ab-testing/" className="link-table">A/B Testing - Optimizely</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/multivariate-testing/" className="link-table">A/B Testing and Multivariate Testing</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.exp-platform.com/Documents/GuideControlledExperiments.pdf" className="link-table">Practical Guide to Controlled Experiments on the Web</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-ab-testing" 
            id="tab-ab-testing-template"
          />
          <label htmlFor="tab-ab-testing-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_klCr2jE=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="A/B Testing Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Questionnaire Section */}
      <main className="case-study">
        <span className="case-tool-title" id="questionnaire">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="questionnaire">
            <span className="tool-title-number">
              <Link to="/empathise/data#questionnaire">Questionnaire</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/research/empathise">Empathise</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/research/empathise/data">Data</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/empathise/data#questionnaire"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-questionnaire" 
            id="tab-questionnaire-overview" 
            defaultChecked
          />
          <label htmlFor="tab-questionnaire-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Data", "20/08/20", questionnaireTemplates, naCreator)}
              <div className="tool-opening">
                <p>
                Questionnaires allow you to collect structured data from a large number of users efficiently. They can include both quantitative (rating scales, multiple choice) and qualitative (open-ended) questions.
                </p>
                
                <p>
                Well-designed questionnaires can provide insights into user demographics, preferences, behaviors, satisfaction levels, and needs at a scale that would be impractical with individual interviews.
                </p>
                
                <p>
                This method is particularly useful for gathering feedback from existing users, validating hypotheses across a large sample, and identifying trends or patterns in user preferences and behaviors.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/questionnaire-surveys/" className="link-table">Questionnaire Surveys: 7 Tips for Creating a Great User Experience</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.usability.gov/how-to-and-tools/methods/online-surveys.html" className="link-table">Online Surveys - Usability.gov</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.interaction-design.org/literature/article/the-dos-and-don-ts-of-survey-design" className="link-table">The Dos and Don'ts of Survey Design</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-questionnaire" 
            id="tab-questionnaire-template"
          />
          <label htmlFor="tab-questionnaire-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_klD5iR8=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Questionnaire Template"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default DataPage; 